// Encrypted media pipeline: client-side encrypt a blob with a per-blob
// AES-256-GCM key, upload the ciphertext to the Supabase 'media' bucket,
// embed the key + path inside the recipient's E2EE envelope. The server
// holds opaque bytes; only the recipient can decrypt.

import { encodeBase64, decodeBase64 } from 'tweetnacl-util';
import type { MediaRef } from '../types';
import { getDb } from './db';

const BUCKET = 'media';

// WebCrypto + TS 5.6 want ArrayBuffer-backed BufferSource specifically, not
// Uint8Array<ArrayBufferLike>. Copy through a fresh ArrayBuffer to guarantee
// the type for the small fields we pass to subtle.encrypt/decrypt/importKey.
function toBuffer(u: Uint8Array): ArrayBuffer {
  const out = new ArrayBuffer(u.byteLength);
  new Uint8Array(out).set(u);
  return out;
}

export async function encryptAndUpload(file: File): Promise<MediaRef> {
  const plaintext = new Uint8Array(await file.arrayBuffer());
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertextBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toBuffer(iv) },
    key,
    toBuffer(plaintext),
  );
  const rawKey = new Uint8Array(await crypto.subtle.exportKey('raw', key));

  const storage_path = `${crypto.randomUUID()}.bin`;
  const { error } = await getDb()
    .storage.from(BUCKET)
    .upload(storage_path, new Blob([ciphertextBuf]), {
      contentType: 'application/octet-stream',
      upsert: false,
    });
  if (error) throw error;

  return {
    storage_path,
    key_b64: encodeBase64(rawKey),
    iv_b64: encodeBase64(iv),
    mime: file.type || 'application/octet-stream',
    size: plaintext.byteLength,
  };
}

export async function downloadAndDecrypt(ref: MediaRef): Promise<Blob> {
  const { data, error } = await getDb().storage.from(BUCKET).download(ref.storage_path);
  if (error) throw error;
  const ciphertext = await data.arrayBuffer();
  const key = await crypto.subtle.importKey(
    'raw',
    toBuffer(decodeBase64(ref.key_b64)),
    { name: 'AES-GCM' },
    false,
    ['decrypt'],
  );
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toBuffer(decodeBase64(ref.iv_b64)) },
    key,
    ciphertext,
  );
  return new Blob([plaintext], { type: ref.mime });
}

export async function deleteUploaded(ref: MediaRef): Promise<void> {
  await getDb().storage.from(BUCKET).remove([ref.storage_path]);
}
